import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PrivilegedChromeProvider } from '@/contexts/PrivilegedChromeContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import { ContactCardProvider } from '@/contexts/ContactCardContext';
import { SyncStatusProvider } from '@/contexts/SyncStatusContext';
import { ConfirmProvider } from '@/contexts/ConfirmContext';
import { TourProvider } from '@/contexts/TourContext';
import { AutoPopupsProvider } from '@/contexts/AutoPopupsContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import GatedRoute from '@/components/GatedRoute';
import BrandedSplash from '@/components/BrandedSplash';
import { captureAttribution } from '@/lib/attribution';
import RetargetingPixels from '@/components/RetargetingPixels';import './i18n';
import DocumentMeta from '@/components/DocumentMeta';
import '@/App.css';

const loadLanding = () => import('@/pages/Landing');
if (typeof window !== 'undefined' && window.location.pathname === '/') {
  loadLanding();
}

const Landing = lazy(loadLanding);
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const Pending = lazy(() => import('@/pages/Pending'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CalendarOverview = lazy(() => import('@/pages/CalendarOverview'));
const EventTypes = lazy(() => import('@/pages/EventTypes'));
const EventTypeChooser = lazy(() => import('@/pages/EventTypeChooser'));
const EventTypeEdit = lazy(() => import('@/pages/EventTypeEdit'));
const Polls = lazy(() => import('@/pages/Polls'));
const PollNew = lazy(() => import('@/pages/PollNew'));
const PollDashboard = lazy(() => import('@/pages/PollDashboard'));
const PollConfirmed = lazy(() => import('@/pages/PollConfirmed'));
const PollVote = lazy(() => import('@/pages/PollVote'));
const QueueNew = lazy(() => import('@/pages/QueueNew'));
const QueueDashboard = lazy(() => import('@/pages/QueueDashboard'));
const QueueBook = lazy(() => import('@/pages/QueueBook'));
const QueueStatus = lazy(() => import('@/pages/QueueStatus'));
const Queues = lazy(() => import('@/pages/Queues'));
const Availability = lazy(() => import('@/pages/Availability'));
const Touring = lazy(() => import('@/pages/Touring'));
const Integrations = lazy(() => import('@/pages/Integrations'));
const Bookings = lazy(() => import('@/pages/Bookings'));
const TemplateBookings = lazy(() => import('@/pages/TemplateBookings'));
const Settings = lazy(() => import('@/pages/Settings'));
const Wallet = lazy(() => import('@/pages/Wallet'));
const EarnCredits = lazy(() => import('@/pages/EarnCredits'));
const Branding = lazy(() => import('@/pages/Branding'));
const PublicBooking = lazy(() => import('@/pages/PublicBooking'));
const PublicBookingTour = lazy(() => import('@/pages/PublicBookingTour'));
const BookingConfirmation = lazy(() => import('@/pages/BookingConfirmation'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const DataProcessingPolicy = lazy(() => import('@/pages/legal/DataProcessingPolicy'));
const PublicOffer = lazy(() => import('@/pages/legal/PublicOffer'));
const MarketingPolicy = lazy(() => import('@/pages/legal/MarketingPolicy'));
const ThirdPartyMessaging = lazy(() => import('@/pages/legal/ThirdPartyMessaging'));
const DataProcessingAgreement = lazy(() => import('@/pages/legal/DataProcessingAgreement'));
const LegalDocVersion = lazy(() => import('@/pages/legal/LegalDocVersion'));
const ZoomGuide = lazy(() => import('@/pages/docs/ZoomGuide'));
const Help = lazy(() => import('@/pages/Help'));
const Tutorials = lazy(() => import('@/pages/Tutorials'));
const ConsentCenter = lazy(() => import('@/pages/ConsentCenter'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const SocialCallback = lazy(() => import('@/pages/SocialCallback'));
const PropertyUseCaseLanding = lazy(() => import('@/pages/PropertyUseCaseLanding'));
const ServiceUseCaseLanding = lazy(() => import('@/pages/ServiceUseCaseLanding'));
const PropertyWizard = lazy(() => import('@/pages/PropertyWizard'));
const PropertyList = lazy(() => import('@/pages/PropertyList'));
const PropertyDashboard = lazy(() => import('@/pages/PropertyDashboard'));
const PropertyView = lazy(() => import('@/pages/PropertyView'));
const PropertyBookingStatus = lazy(() => import('@/pages/PropertyBookingStatus'));
const RealtyReview = lazy(() => import('@/pages/RealtyReview'));
const SaveEvent = lazy(() => import('@/pages/SaveEvent'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const QuickInvitePublic = lazy(() => import('@/pages/QuickInvitePublic'));
const QuickInvites = lazy(() => import('@/pages/QuickInvites'));
const QuickInviteEditor = lazy(() => import('@/pages/QuickInviteEditor'));
const ReschedulePublic = lazy(() => import('@/pages/ReschedulePublic'));
const Contacts = lazy(() => import('@/pages/Contacts'));
const Admin = lazy(() => import('@/pages/Admin'));
const UserActivity = lazy(() => import('@/pages/admin/UserActivity'));
const Support = lazy(() => import('@/pages/Support'));
const Services = lazy(() => import('@/pages/Services'));
const ServiceWizard = lazy(() => import('@/pages/ServiceWizard'));
const HostAction = lazy(() => import('@/pages/HostAction'));
const LandlordManage = lazy(() => import('@/pages/LandlordManage'));
const LandlordAction = lazy(() => import('@/pages/LandlordAction'));

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <BrandedSplash />;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

// KAL-81 — scroll-restoration: open every route from the top (unless an
// explicit #anchor is targeted, which the destination page handles itself).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0 });
    document.getElementById('app-scroll')?.scrollTo({ top: 0, left: 0 });
  }, [pathname, hash]);
  return null;
}

// Persist ?ref / utm attribution from any entry point so a signup that happens
// later (e.g. after browsing the landing) still credits the referring host.
function AttributionCapture() {
  const { search } = useLocation();
  useEffect(() => { captureAttribution(search); }, [search]);
  return null;
}

export default function App() {
  useEffect(() => { window.__hideAppSplash?.(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PrivilegedChromeProvider>
          <AutoPopupsProvider>
          <NotificationsProvider>
          <SyncStatusProvider>
          <ConfirmProvider>
          <TourProvider>
          <ContactCardProvider>
          <ScrollToTop />
          <DocumentMeta />
          <AttributionCapture />
          <RetargetingPixels />
          <Suspense fallback={<BrandedSplash />}>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/features" element={<Navigate to="/#features" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pending" element={<Pending />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/social" element={<SocialCallback />} />
            <Route path="/use-cases/property" element={<PropertyUseCaseLanding />} />
            <Route path="/use-cases/:kind" element={<ServiceUseCaseLanding />} />
            <Route path="/properties" element={<ProtectedRoute><PropertyList /></ProtectedRoute>} />
            <Route path="/p-new" element={<ProtectedRoute><PropertyWizard /></ProtectedRoute>} />
            <Route path="/p-edit/:id" element={<ProtectedRoute><PropertyWizard /></ProtectedRoute>} />
            <Route path="/p-dashboard/:id" element={<ProtectedRoute><PropertyDashboard /></ProtectedRoute>} />
            <Route path="/review-realty-listings" element={<ProtectedRoute><RealtyReview /></ProtectedRoute>} />
            <Route path="/gold-selection" element={<Navigate to="/review-realty-listings" replace />} />
            <Route path="/p/status/:token" element={<PropertyBookingStatus />} />
            <Route path="/save-event" element={<SaveEvent />} />
            <Route path="/host/r/:token" element={<HostAction />} />
            <Route path="/landlord/:token" element={<LandlordManage />} />
            <Route path="/landlord/a/:actionToken" element={<LandlordAction />} />
            <Route path="/p/:token" element={<PropertyView />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/legal/data-processing" element={<DataProcessingPolicy />} />
            <Route path="/legal/offer" element={<PublicOffer />} />
            <Route path="/legal/marketing" element={<MarketingPolicy />} />
            <Route path="/legal/third-party-messaging" element={<ThirdPartyMessaging />} />
            <Route path="/legal/dpa" element={<DataProcessingAgreement />} />
            <Route path="/legal/:docKey/v/:versionId" element={<LegalDocVersion />} />
            <Route path="/docs/zoom" element={<ZoomGuide />} />
            <Route path="/help" element={<Help />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/consents" element={<ProtectedRoute><ConsentCenter /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarOverview /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/quick-invites" element={<GatedRoute><QuickInvites /></GatedRoute>} />
            <Route path="/quick-invites/new" element={<GatedRoute><QuickInviteEditor /></GatedRoute>} />
            <Route path="/quick-invites/:id/edit" element={<ProtectedRoute><QuickInviteEditor /></ProtectedRoute>} />
            <Route path="/qi/:token" element={<QuickInvitePublic />} />
            <Route path="/reschedule/:token" element={<ReschedulePublic />} />            <Route path="/event-types" element={<ProtectedRoute><EventTypes /></ProtectedRoute>} />
            <Route path="/event-types/new" element={<ProtectedRoute><EventTypeChooser /></ProtectedRoute>} />
            <Route path="/event-types/new/availability" element={<GatedRoute><EventTypeEdit /></GatedRoute>} />
            <Route path="/event-types/:id/edit" element={<ProtectedRoute><EventTypeEdit /></ProtectedRoute>} />
            <Route path="/event-types/:id/bookings" element={<ProtectedRoute><TemplateBookings /></ProtectedRoute>} />
            <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
            <Route path="/services/kind/:kind" element={<ProtectedRoute><Services /></ProtectedRoute>} />
            <Route path="/services/new/:kind" element={<GatedRoute><ServiceWizard /></GatedRoute>} />
            <Route path="/services/:id/edit" element={<ProtectedRoute><ServiceWizard /></ProtectedRoute>} />
            <Route path="/polls" element={<ProtectedRoute><Polls /></ProtectedRoute>} />
            <Route path="/polls/new" element={<GatedRoute><PollNew /></GatedRoute>} />
            <Route path="/polls/:id/edit" element={<ProtectedRoute><PollNew /></ProtectedRoute>} />
            <Route path="/polls/:id" element={<ProtectedRoute><PollDashboard /></ProtectedRoute>} />
            <Route path="/polls/:id/confirmed" element={<ProtectedRoute><PollConfirmed /></ProtectedRoute>} />
            <Route path="/vote/:token" element={<PollVote />} />
            <Route path="/queue/new" element={<GatedRoute><QueueNew /></GatedRoute>} />
            <Route path="/queues" element={<ProtectedRoute><Queues /></ProtectedRoute>} />
            <Route path="/queue/:id" element={<ProtectedRoute><QueueDashboard /></ProtectedRoute>} />
            <Route path="/q/:token" element={<QueueBook />} />
            <Route path="/q/status/:bookingId" element={<QueueStatus />} />
            <Route path="/availability" element={<ProtectedRoute><Availability /></ProtectedRoute>} />
            <Route path="/touring" element={<GatedRoute><Touring /></GatedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
            <Route path="/calendars" element={<Navigate to="/integrations" replace />} />
            <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
            <Route path="/planning" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/admin/users/:id" element={<ProtectedRoute><UserActivity /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/billing" element={<Navigate to="/wallet" replace />} />
            <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/earn" element={<ProtectedRoute><EarnCredits /></ProtectedRoute>} />
            <Route path="/branding" element={<ProtectedRoute><Branding /></ProtectedRoute>} />
            <Route path="/book/:slug/:eventSlug" element={<PublicBooking />} />
            <Route path="/tour/:slug" element={<PublicBookingTour />} />
            <Route path="/book/:slug/:eventSlug/confirmation/:bookingId" element={<BookingConfirmation />} />
            <Route path="/m/:bookingId" element={<BookingConfirmation />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Toaster richColors position="top-right" />
          </ContactCardProvider>
          </TourProvider>
          </ConfirmProvider>
          </SyncStatusProvider>
          </NotificationsProvider>
          </AutoPopupsProvider>
          </PrivilegedChromeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
